import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Button, Paper,
  Alert, LinearProgress, Chip, Divider, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip, Stack
} from '@mui/material';
import {
  People as PeopleIcon,
  PersonAdd as NewEnrollmentIcon,
  Refresh as RefreshIcon,
  Sync as SyncIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  CreditCard as CreditCardIcon,
  EmojiEvents as LaureatIcon,
  ArrowForward as ArrowForwardIcon,
  AccessTime as ClockIcon,
  CalendarToday as CalendarIcon,
  Download as DownloadIcon,
  RateReview as ReclamationIcon,
  School as ProgramsIcon,
  TrendingUp as SuccessRateIcon,
  Login as LoginIcon,
  Grade as GradeIcon,
  TrendingDown as EchecIcon,
  Star as MentionIcon,
} from '@mui/icons-material';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer,
} from 'recharts';
import { adminAPI } from '../../services/api';

// ─── color palette (matches StatisticsPage.jsx) ────────────────────────────
const CYCLE_COLORS = { 'Licence': '#3498db', 'Master': '#9b59b6', 'Doctorat': '#1abc9c', 'Non renseigné': '#94a3b8' };
const RECLAM_META = {
  pending:     { label: 'En attente', color: '#f39c12' },
  in_progress: { label: 'En cours',   color: '#3498db' },
  resolved:    { label: 'Résolues',   color: '#2ecc71' },
  rejected:    { label: 'Rejetées',   color: '#e74c3c' },
};

// ─── helpers ────────────────────────────────────────────────────────────────

const fmt = (d) => {
  if (!d) return 'Never';
  const date = new Date(d);
  return date.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
};

const hoursSince = (d) => {
  if (!d) return 999;
  return (Date.now() - new Date(d).getTime()) / 36e5;
};

const syncColor = (status) => {
  if (status === 'success') return 'success';
  if (status === 'error') return 'error';
  if (status === 'started') return 'warning';
  return 'default';
};

// ─── sub-components ─────────────────────────────────────────────────────────

const KpiCard = ({ title, value, subtitle, icon, gradient, onClick, badge, loading }) => (
  <Card
    onClick={onClick}
    sx={{
      height: '100%', position: 'relative', overflow: 'hidden',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': onClick ? { transform: 'translateY(-3px)', boxShadow: '0 10px 30px rgba(0,0,0,0.12)' } : {}
    }}
  >
    {/* coloured accent strip */}
    <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: gradient }} />

    <CardContent sx={{ pt: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box
          sx={{
            width: 52, height: 52, borderRadius: 2,
            background: gradient, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            color: 'white', boxShadow: '0 4px 14px rgba(0,0,0,0.15)'
          }}
        >
          {icon}
        </Box>
        {badge !== undefined && badge > 0 && (
          <Chip
            label={badge}
            color="error"
            size="small"
            sx={{ fontWeight: 700, fontSize: '0.8rem' }}
          />
        )}
      </Box>

      <Typography variant="h3" fontWeight={700} sx={{ mt: 2, mb: 0.5, lineHeight: 1 }}>
        {loading ? <LinearProgress sx={{ mt: 1.5, height: 6, borderRadius: 3 }} /> : value}
      </Typography>
      <Typography variant="subtitle2" fontWeight={600} color="text.primary">
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          {subtitle}
        </Typography>
      )}

      {onClick && (
        <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="caption" color="primary" fontWeight={600}>View details</Typography>
          <ArrowForwardIcon sx={{ fontSize: 12, color: 'primary.main' }} />
        </Box>
      )}
    </CardContent>
  </Card>
);

const SectionTitle = ({ children }) => (
  <Typography variant="h6" fontWeight={700} sx={{ mb: 2, mt: 1, color: 'text.primary' }}>
    {children}
  </Typography>
);

const MiniBar = ({ value, max, color }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Box sx={{ flexGrow: 1 }}>
      <LinearProgress
        variant="determinate"
        value={max > 0 ? Math.min((value / max) * 100, 100) : 0}
        sx={{
          height: 8, borderRadius: 4,
          bgcolor: `${color}22`,
          '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 4 }
        }}
      />
    </Box>
    <Typography variant="caption" fontWeight={600} sx={{ minWidth: 36, textAlign: 'right' }}>
      {value}
    </Typography>
  </Box>
);

// Small stat used inside the Performance Académique card
const MiniStat = ({ label, value, color, icon }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: 1.25, borderRadius: 2, bgcolor: `${color}0d`, border: `1px solid ${color}30` }}>
    <Box sx={{ width: 32, height: 32, borderRadius: 1.5, bgcolor: `${color}20`, color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body1" fontWeight={700} sx={{ lineHeight: 1.1 }}>{value}</Typography>
      <Typography variant="caption" color="text.secondary" noWrap display="block">{label}</Typography>
    </Box>
  </Box>
);

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Paper elevation={3} sx={{ p: 1.25, borderRadius: 2 }}>
      {label && <Typography variant="caption" fontWeight={700} display="block">{label}</Typography>}
      {payload.map((p, i) => (
        <Typography key={i} variant="caption" display="block" sx={{ color: p.color || p.payload?.fill }}>
          {p.name}: {typeof p.value === 'number' ? p.value.toLocaleString('fr-FR') : p.value}
        </Typography>
      ))}
    </Paper>
  );
};

// ─── main component ──────────────────────────────────────────────────────────

const AdminOverview = ({ onNavigate }) => {
  const [stats, setStats] = useState(null);
  const [overview, setOverview] = useState(null);
  const [sync, setSync] = useState(null);
  const [cardRequests, setCardRequests] = useState([]);
  const [examConflicts, setExamConflicts] = useState(0);
  const [laureatStats, setLaureatStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [now, setNow] = useState(new Date());
  const [exportingCF, setExportingCF] = useState(false);

  // live clock
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, ov, sy, cr, ec, ls] = await Promise.allSettled([
        adminAPI.getDashboardStats(),
        adminAPI.getDataOverview(),
        adminAPI.getSyncStatus(4),
        adminAPI.getStudentCardRequests(),
        adminAPI.getExamConflictsCount(),
        adminAPI.getLaureatStats()
      ]);

      if (s.status === 'fulfilled') setStats(s.value);
      if (ov.status === 'fulfilled') setOverview(ov.value);
      if (sy.status === 'fulfilled') setSync(sy.value);
      if (cr.status === 'fulfilled') setCardRequests(cr.value?.requests || []);
      if (ec.status === 'fulfilled') setExamConflicts(ec.value || 0);
      if (ls.status === 'fulfilled') setLaureatStats(ls.value);

      if (s.status === 'rejected') setError('Failed to load core statistics.');
    } catch (e) {
      setError('Dashboard load failed.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleExportCF = async () => {
    try {
      setExportingCF(true);
      await adminAPI.exportRattrapageCF(stats?.current_year || null);
    } catch {
      setError('Échec de l\'export rattrapage CF.');
    } finally {
      setExportingCF(false);
    }
  };

  // derived values
  const pendingCards = cardRequests.filter(r => r.status === 'pending').length;
  const pendingReclamations = stats?.overview?.pending_reclamations || 0;
  const totalLaureats = laureatStats?.byYear?.reduce((a, r) => a + parseInt(r.count), 0) || 0;
  const lastSyncHours = hoursSince(stats?.recent_activity?.last_sync);
  const syncOld = lastSyncHours > 24;

  const maxStudentsByProgram = overview
    ? Math.max(...(overview.students_by_program || []).map(r => parseInt(r.count)), 1)
    : 1;

  // Chronological (oldest → newest) enrollment trend for the bar chart
  const enrollmentTrend = overview
    ? [...(overview.students_by_year || [])]
        .slice(0, 6)
        .reverse()
        .map(r => ({ year: `${r.cod_anu}`, Étudiants: parseInt(r.count) }))
    : [];

  const cycleData = (stats?.overview?.cycle_breakdown || []).map(r => ({
    name: r.cycle,
    value: parseInt(r.count),
  }));

  const topPrograms = (overview?.students_by_program || []).slice(0, 5);

  const reclamStatus = stats?.overview?.reclamations_status || { pending: 0, in_progress: 0, resolved: 0, rejected: 0 };
  const reclamData = Object.entries(reclamStatus)
    .filter(([, count]) => count > 0)
    .map(([key, count]) => ({ name: RECLAM_META[key]?.label || key, value: count, key }));
  const reclamTotal = Object.values(reclamStatus).reduce((a, b) => a + b, 0);

  // ── render ───────────────────────────────────────────────────────────────

  return (
    <Box>

      {/* ── HEADER ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="primary">
            Tableau de Bord
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CalendarIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {now.toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ClockIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
              </Typography>
            </Box>
            {stats && (
              <Chip label={`Année ${stats.current_year} - ${parseInt(stats.current_year) + 1}`} size="small" color="primary" variant="outlined" />
            )}
          </Stack>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            color="warning"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleExportCF}
            disabled={exportingCF}
          >
            {exportingCF ? 'Export...' : 'Export Rattrapage CF'}
          </Button>
          <Tooltip title="Refresh data">
            <IconButton onClick={load} color="primary" sx={{ bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* ── ERROR ── */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ── ALERTS / ACTION REQUIRED ── */}
      {!loading && (examConflicts > 0 || pendingCards > 0 || pendingReclamations > 0 || syncOld) && (
        <Box sx={{ mb: 3 }}>
          <SectionTitle>Alertes & Actions Requises</SectionTitle>
          <Stack spacing={1}>
            {pendingReclamations > 0 && (
              <Alert
                severity="warning"
                icon={<ReclamationIcon />}
                action={
                  <Button size="small" color="warning" onClick={() => onNavigate('grade-reclamations')}>
                    Traiter les réclamations
                  </Button>
                }
              >
                <strong>{pendingReclamations} réclamation(s) de notes</strong> en attente de réponse.
              </Alert>
            )}
            {examConflicts > 0 && (
              <Alert
                severity="error"
                icon={<ErrorIcon />}
                action={
                  <Button size="small" color="error" onClick={() => onNavigate('exams')}>
                    Voir les examens
                  </Button>
                }
              >
                <strong>{examConflicts} conflit(s) d'examens</strong> — des étudiants ont des examens qui se chevauchent.
              </Alert>
            )}
            {pendingCards > 0 && (
              <Alert
                severity="warning"
                icon={<CreditCardIcon />}
                action={
                  <Button size="small" color="warning" onClick={() => onNavigate('card-requests')}>
                    Traiter les demandes
                  </Button>
                }
              >
                <strong>{pendingCards} demande(s) de carte étudiant</strong> en attente de validation.
              </Alert>
            )}
            {syncOld && (
              <Alert
                severity="warning"
                icon={<SyncIcon />}
                action={
                  <Button size="small" color="warning" onClick={() => onNavigate('sync')}>
                    Synchroniser
                  </Button>
                }
              >
                <strong>Synchronisation ancienne</strong> — dernière sync il y a {Math.floor(lastSyncHours)}h. Une mise à jour est recommandée.
              </Alert>
            )}
          </Stack>
        </Box>
      )}

      {/* ── KPI CARDS ROW ──
          At-a-glance statistics only. Items needing admin action (exam conflicts,
          pending card requests, pending reclamations) live in the Alerts section
          above instead — showing them here too was pure duplication. */}
      <SectionTitle>Vue Générale — Année {stats?.current_year}</SectionTitle>
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <KpiCard
            title="Étudiants actifs"
            value={loading ? '…' : (stats?.overview?.total_students?.toLocaleString() || '—')}
            subtitle={`Année ${stats?.current_year}`}
            icon={<PeopleIcon />}
            gradient="linear-gradient(135deg, #3498db, #2980b9)"
            onClick={() => onNavigate('students')}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <KpiCard
            title="Nouveaux inscrits"
            value={loading ? '…' : (stats?.overview?.new_enrollments?.toLocaleString() ?? '—')}
            subtitle="1ère inscription cette année"
            icon={<NewEnrollmentIcon />}
            gradient="linear-gradient(135deg, #16a085, #1abc9c)"
            onClick={() => onNavigate('students')}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <KpiCard
            title="Programmes actifs"
            value={loading ? '…' : (stats?.overview?.total_programs?.toLocaleString() || '—')}
            subtitle="Filières & diplômes"
            icon={<ProgramsIcon />}
            gradient="linear-gradient(135deg, #8e44ad, #6c3483)"
            onClick={() => onNavigate('pedagogy-structure')}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <KpiCard
            title="Taux de réussite"
            value={loading ? '…' : (stats?.overview?.grade_stats?.success_rate != null ? `${stats.overview.grade_stats.success_rate}%` : '—')}
            subtitle="Semestres impairs — Automne"
            icon={<SuccessRateIcon />}
            gradient="linear-gradient(135deg, #2ecc71, #27ae60)"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <KpiCard
            title="Diplômés"
            value={loading ? '…' : totalLaureats.toLocaleString()}
            subtitle={laureatStats ? `${laureatStats.byYear?.length || 0} promotions` : ''}
            icon={<LaureatIcon />}
            gradient="linear-gradient(135deg, #f39c12, #e67e22)"
            onClick={() => onNavigate('laureats')}
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* ── ENROLLMENT TREND + CYCLE BREAKDOWN ── */}
      {overview && stats && (
        <>
          <SectionTitle>Inscriptions</SectionTitle>
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid item xs={12} md={8}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    Évolution des Inscriptions
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Étudiants inscrits (situation administrative) par année universitaire
                  </Typography>
                  <Box sx={{ height: 260, mt: 1 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={enrollmentTrend} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef1f4" />
                        <XAxis dataKey="year" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} width={45} />
                        <RTooltip content={<ChartTooltip />} />
                        <Bar dataKey="Étudiants" fill="#3498db" radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    Répartition par Cycle
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    D'après le code diplôme (JL/JM/JD)
                  </Typography>
                  <Box sx={{ height: 200 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={cycleData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={78} paddingAngle={2}>
                          {cycleData.map((entry, i) => (
                            <Cell key={i} fill={CYCLE_COLORS[entry.name] || '#94a3b8'} />
                          ))}
                        </Pie>
                        <RTooltip content={<ChartTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                  <Stack spacing={0.75} sx={{ mt: 1 }}>
                    {cycleData.map((c) => (
                      <Box key={c.name} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: CYCLE_COLORS[c.name] || '#94a3b8' }} />
                          <Typography variant="caption">{c.name}</Typography>
                        </Box>
                        <Typography variant="caption" fontWeight={700}>{c.value.toLocaleString()}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}

      {/* ── TOP FILIÈRES + PERFORMANCE + RÉCLAMATIONS ── */}
      {overview && stats && (
        <>
          <SectionTitle>Performance & Filières</SectionTitle>
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            {/* Top 5 diploma codes */}
            <Grid item xs={12} md={5}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      Diplômes — Top 5
                    </Typography>
                    <Button size="small" variant="text" onClick={() => onNavigate('students')} endIcon={<ArrowForwardIcon />}>
                      Voir tout
                    </Button>
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                    D'après l'inscription administrative (cod_dip), pas la Structure Pédagogique
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Stack spacing={1.2}>
                    {topPrograms.map((row, i) => {
                      const noDiplomaCode = !row.cod_dip;
                      return (
                        <Box key={row.cod_dip || 'blank'}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5, gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                              <Chip
                                label={noDiplomaCode ? '?' : row.cod_dip}
                                size="small"
                                sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, fontFamily: 'monospace',
                                  bgcolor: noDiplomaCode ? '#94a3b820' : (i < 3 ? '#9b59b620' : '#3498db15'),
                                  color: noDiplomaCode ? '#64748b' : (i < 3 ? '#9b59b6' : '#3498db'),
                                  borderRadius: 1, flexShrink: 0 }}
                              />
                              <Typography variant="body2" fontWeight={600} noWrap
                                sx={noDiplomaCode ? { fontStyle: 'italic', color: 'text.secondary' } : undefined}
                                title={noDiplomaCode
                                  ? "Étudiants inscrits dont le code diplôme (cod_dip) n'est pas renseigné côté Oracle"
                                  : (row.lib_dip || row.cod_dip)}>
                                {noDiplomaCode ? 'Diplôme non renseigné' : (row.lib_dip || row.cod_dip)}
                              </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                              {parseInt(row.count).toLocaleString()}
                            </Typography>
                          </Box>
                          <MiniBar
                            value={parseInt(row.count)}
                            max={maxStudentsByProgram}
                            color={noDiplomaCode ? '#94a3b8' : (i < 3 ? '#9b59b6' : '#3498db')}
                          />
                        </Box>
                      );
                    })}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Performance académique */}
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    Performance Académique
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                    Semestres impairs — Automne
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={1.5}>
                    <Grid item xs={6}>
                      <MiniStat label="Moyenne générale" value={`${stats.overview.grade_stats.average}/20`} color="#9b59b6" icon={<GradeIcon sx={{ fontSize: 18 }} />} />
                    </Grid>
                    <Grid item xs={6}>
                      <MiniStat label="Taux de réussite" value={`${stats.overview.grade_stats.success_rate}%`} color="#2ecc71" icon={<SuccessRateIcon sx={{ fontSize: 18 }} />} />
                    </Grid>
                    <Grid item xs={6}>
                      <MiniStat label="Mentions (≥12)" value={stats.overview.grade_stats.mentions.toLocaleString()} color="#f39c12" icon={<MentionIcon sx={{ fontSize: 18 }} />} />
                    </Grid>
                    <Grid item xs={6}>
                      <MiniStat label="Échecs (<10)" value={stats.overview.grade_stats.echecs.toLocaleString()} color="#e74c3c" icon={<EchecIcon sx={{ fontSize: 18 }} />} />
                    </Grid>
                  </Grid>
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption">Session Normale</Typography>
                      <Typography variant="caption" fontWeight={700}>{stats.overview.grade_stats.sessions.normal.toLocaleString()}</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={stats.overview.grade_stats.total > 0 ? (stats.overview.grade_stats.sessions.normal / stats.overview.grade_stats.total) * 100 : 0}
                      sx={{ height: 6, borderRadius: 3, bgcolor: '#e8f4fd', '& .MuiLinearProgress-bar': { bgcolor: '#3498db' } }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, mb: 0.5 }}>
                      <Typography variant="caption">Session Rattrapage</Typography>
                      <Typography variant="caption" fontWeight={700}>{stats.overview.grade_stats.sessions.rattrapage.toLocaleString()}</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={stats.overview.grade_stats.total > 0 ? (stats.overview.grade_stats.sessions.rattrapage / stats.overview.grade_stats.total) * 100 : 0}
                      sx={{ height: 6, borderRadius: 3, bgcolor: '#fef9e7', '& .MuiLinearProgress-bar': { bgcolor: '#f39c12' } }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Statut des réclamations */}
            <Grid item xs={12} md={3}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle1" fontWeight={700}>Réclamations</Typography>
                    <Button size="small" variant="text" onClick={() => onNavigate('grade-reclamations')}>Gérer</Button>
                  </Box>
                  <Divider sx={{ mb: 1.5, mt: 1 }} />
                  {reclamTotal > 0 ? (
                    <>
                      <Box sx={{ height: 170, position: 'relative' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie data={reclamData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={68} paddingAngle={2}>
                              {reclamData.map((entry, i) => (
                                <Cell key={i} fill={RECLAM_META[entry.key]?.color || '#94a3b8'} />
                              ))}
                            </Pie>
                            <RTooltip content={<ChartTooltip />} />
                          </PieChart>
                        </ResponsiveContainer>
                        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', pointerEvents: 'none' }}>
                          <Typography variant="h6" fontWeight={700} lineHeight={1}>{reclamTotal}</Typography>
                          <Typography variant="caption" color="text.secondary">Total</Typography>
                        </Box>
                      </Box>
                      <Stack spacing={0.6} sx={{ mt: 1 }}>
                        {Object.entries(reclamStatus).filter(([, c]) => c > 0).map(([key, count]) => (
                          <Box key={key} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: RECLAM_META[key]?.color }} />
                              <Typography variant="caption">{RECLAM_META[key]?.label}</Typography>
                            </Box>
                            <Typography variant="caption" fontWeight={700}>{count}</Typography>
                          </Box>
                        ))}
                      </Stack>
                    </>
                  ) : (
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                      <CheckCircleIcon sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">Aucune réclamation</Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}

      {/* ── LAUREATS + SYNC + CONNEXIONS ── */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        {/* Laureats by Year */}
        {laureatStats && laureatStats.byYear?.length > 0 && (
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Lauréats par Promotion
                  </Typography>
                  <Chip
                    icon={<LaureatIcon sx={{ fontSize: 14 }} />}
                    label={`${totalLaureats} total`}
                    color="warning"
                    size="small"
                    variant="outlined"
                  />
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={1}>
                  {laureatStats.byYear.slice(0, 6).map((row) => (
                    <Box key={row.cod_anu} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">{row.cod_anu} – {parseInt(row.cod_anu) + 1}</Typography>
                      <Chip label={row.count} size="small" color="warning" variant="outlined" />
                    </Box>
                  ))}
                </Stack>
                {laureatStats.multiDiplomaCount > 0 && (
                  <Alert severity="info" sx={{ mt: 1.5, py: 0.5 }}>
                    {laureatStats.multiDiplomaCount} étudiant(s) avec double diplôme
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Recent Sync + Connexions */}
        <Grid item xs={12} md={laureatStats?.byYear?.length > 0 ? 8 : 12}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" fontWeight={700}>Historique de Synchronisation</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    icon={<LoginIcon sx={{ fontSize: 14 }} />}
                    label={`${stats?.overview?.logins_today ?? 0} connexion(s) aujourd'hui`}
                    size="small"
                    variant="outlined"
                    color="primary"
                  />
                  <Button size="small" variant="text" onClick={() => onNavigate('sync')} endIcon={<ArrowForwardIcon />}>
                    Voir tout
                  </Button>
                </Stack>
              </Box>
              <Divider sx={{ mb: 2 }} />
              {sync ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Statut</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">Enregistrements</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Date / Heure</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sync.recent_syncs?.slice(0, 4).map((s, i) => (
                        <TableRow key={i} sx={{ '&:last-child td': { border: 0 } }}>
                          <TableCell>{s.sync_type}</TableCell>
                          <TableCell>
                            <Chip
                              label={s.sync_status}
                              color={syncColor(s.sync_status)}
                              size="small"
                              icon={s.sync_status === 'success' ? <CheckCircleIcon /> : <WarningIcon />}
                            />
                          </TableCell>
                          <TableCell align="right">{(s.records_processed || 0).toLocaleString()}</TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {fmt(s.sync_timestamp)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary">Aucun historique de synchronisation.</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

    </Box>
  );
};

export default AdminOverview;
